<template>
  <div class="pa-4">
    <div v-if="loading" class="text-center">
      <h3>{{ $t("message.checking_connection") }}...</h3>
      <circles-loader></circles-loader>
    </div>
    <div v-else>
      <div v-if="!loading">
        <v-card elevation="0" v-if="product" class="text-center">
          <v-avatar size="200" v-if="product && product.type == 'good' && product.image">
            <ImageDisplay v-if="product.image" :src="product.image" />
          </v-avatar>
          <v-card-text v-if="product">
            <div class="text-h6">
              <v-icon v-if="product.type == 'service'">mdi-credit-card-outline</v-icon>
              {{ product.name }}
            </div>
            <div class="font-weight-bold text-h6">{{ product.amountDecimal }} {{ currency }}</div>
            <div v-if="product.taxBehavior" class="accent--text">
              <span>{{ $t("message.tax_settings") }}:</span>
              <span class="ml-1">{{ product.taxBehavior == 'exclusive' ? $t("message.exclusive") :
                $t("message.inclusive") }}</span>
            </div>
            <div v-if="product.type == 'service'">{{ getIntervalDisplay(product.interval, product.customDaysInterval) }}
            </div>
            <div v-else-if="product.membershipDays > 0">
              {{ product.membershipDays }} {{ $t("message.days") }}
            </div>
            <div v-else-if="product.membershipMonths > 0">
              {{ product.membershipMonths }} {{ $t("message.months") }}
            </div>
            <div v-else-if="product.membershipCheckins > 0">
              {{ product.membershipCheckins }} {{ $t("message.checkins") }}
            </div>
            <div v-if="
              product.type == 'good' &&
              !product.unlimited &&
              product.availableQuantity != null &&
              product.availableQuantity >= 0
            ">{{ product.availableQuantity }}
              {{ $t("message.available") }}
            </div>
            <div v-if="product.altStripeAccount" class="accent--text">
              <span>{{ $t("message.alternative_stripe_account") }}:</span>
              <span class="ml-1">{{ product.altStripeAccount }}</span>
            </div>
          </v-card-text>
        </v-card>
        <v-card v-if="(assignToMember || assignToVisitor) && productId != 'package'" elevation="0" outlined
          class="mt-2">
          <v-card-text>
            <v-autocomplete v-model="selectedSubscription" :items="subscriptions"
              :label="$t('message.pick_subscription')" item-text="text" item-value="value" clearable outlined dense>
              <template v-slot:item="data">
                <template>
                  <v-list-item-content v-if="data.item">
                    <v-list-item-title v-html="data.item.text"></v-list-item-title>
                    <v-list-item-subtitle>
                      <span>{{ currencySymbol }}{{ data.item.amountDecimal }}</span> -
                      {{ data.item.automatic ? $t('message.interval_' + data.item.frequency) :
                        $t('message.manual') }}
                      <span v-if="data.item.customDaysInterval">({{ data.item.customDaysInterval }} {{
                        $t('message.days') }})</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template>
            </v-autocomplete>
            <v-autocomplete hide-details @change="selectProductsPackage"
              v-if="availablePackages && availablePackages.length" v-model="selectedPackage" :items="availablePackages"
              :label="$t('message.pick_package')" item-text="name" item-value="_id" clearable outlined dense>
              <template v-slot:item="data">
                <template>
                  <v-list-item-content v-if="data.item">
                    <v-list-item-title v-html="data.item.name"></v-list-item-title>
                    <v-list-item-subtitle>{{ data.item.description }}</v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template>
            </v-autocomplete>
          </v-card-text>
        </v-card>
        <v-card elevation="0" outlined class="mt-2">
          <v-card-text>
            <v-autocomplete hide-details v-if="!assignToVisitor" v-model="selectedMember" :items="members"
              :label="$t('message.pick_member')" item-text="text" item-value="value" clearable outlined
              dense></v-autocomplete>
            <div class="text-center" v-if="product && product.type == 'good' && !product.isMembership">{{
              $t("message.or") }}</div>
            <v-text-field :disabled="assignToVisitor" class="mt-4" outlined dense
              v-if="(product && product.type == 'good' && !product.isMembership) || assignToVisitor" label="Email"
              v-model="email"></v-text-field>
            <div v-if="!checkoutInitialized && product && product.type == 'good'" v-show="false">
              <v-text-field v-if="product.type == 'good'" style="width:10%" outlined dense :rules="[
                () => !isNaN(product.amount) || $t('message.not_a_number'),
              ]" :label="$t('message.quantity')" v-model="quantity"></v-text-field>
              <span style="color:red" v-if="!validQuantity">
                {{
                  $t("message.invalid_quantity")
                }}
              </span>
            </div>
          </v-card-text>
        </v-card>
        <v-expansion-panels v-model="extraOptionsPanel" flat class="mt-2" v-if="product">
          <v-expansion-panel :class="$store.state.theme == 'light' ? 'border-light' : 'border-dark'">
            <v-expansion-panel-header class="text-left">
              {{ $t('message.view_more_options') }}
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div v-if="product && product.type == 'service'">
                <v-card-text class="mt-0 pt-0">
                  <v-switch hide-details v-model="startInFuture" :label="$t('message.start_in_future')"></v-switch>
                  <v-switch hide-details v-if="academyBillDay && isMonthlyMembership()" v-model="startInAcademyDate"
                    :label="$t('message.use_academy_bill_day')"></v-switch>
                  <v-switch hide-details
                    v-else-if="startInFuture && !isCustomAchPaymentOption() && paymentsModule != 'easypay'"
                    v-model="addProratedAmount" :label="$t('message.add_prorated_amount')"></v-switch>
                </v-card-text>
                <v-card-text v-if="startInFuture">
                  <v-alert type="warning" v-if="isCustomAchPaymentOption()">
                    When setting up a debit payment for a membership that starts in the future, <b>a one-time 1 {{
                      currency
                      }}</b> charge
                    will
                    occur at the time you or the user enter the bank information.
                    This is required to comply with mandates due to banking regulations in your country.
                  </v-alert>
                  <v-alert type="info">
                    {{ $t("message.trial_explained") }}
                  </v-alert>
                  <v-alert type="info" v-if="startInFuture && addProratedAmount && trialPeriodDate">
                    {{ $t("message.prorated_bill_explained") }}: <b>{{ proratedAmount }} {{ currency }}</b>
                    <span class="text-overline ml-2">({{ proratedDays }} {{ $t("message.days") }})</span>
                  </v-alert>

                  <div class="mt-2">
                    <div class="text-subtitle-1">{{ $t('message.subscription_start_date') }}</div>
                    <DatePicker :show-time="false" v-model="trialPeriodDate" :past-event="false">
                    </DatePicker>
                  </div>
                </v-card-text>
                <div v-if="startInAcademyDate">
                  <div>
                    <v-text-field outlined dense :disabled="true" :label="$t('message.next_academy_bill_day')"
                      v-model="futureBillDateString">
                    </v-text-field>
                    <span>
                      <v-icon size="20">mdi-information-outline</v-icon>
                      {{ $t("message.prorated_bill_explained") }}: <b>{{ proratedAmount }} {{ currency }}</b>
                      <span class="text-overline ml-2">({{ proratedDays }} {{ $t("message.days") }})</span>
                    </span>
                  </div>
                </div>
                <v-divider></v-divider>
              </div>

              <div style="margin-top: 10px" v-if="
                !checkoutInitialized &&
                product &&
                product.type == 'good' &&
                selectedMember
              ">
                <v-card-text v-if="product.isMembership" class="pt-0">
                  <v-switch :disabled="true" v-model="isSubscription" :label="$t('message.count_as_subscription')">
                  </v-switch>
                  <v-switch v-if="this.product.type == 'good'" v-model="product.manualRecurrence"
                    :label="$t('message.keep_membership_visible')">
                  </v-switch>
                  <v-alert type="info" v-if="product.manualRecurrence">{{
                    $t('message.keep_membership_visible_explained')
                  }}</v-alert>
                  <div v-if="product.type == 'good' && isSubscription">
                    <v-switch :label="$t('message.different_transaction_date')"
                      v-model="subscriptionStartDateCheckbox"></v-switch>
                    <div v-if="subscriptionStartDateCheckbox" class="ml-2">
                      <DatePicker :show-time="false" v-model="subscriptionStartDate" :no-boundries="true">
                      </DatePicker>
                    </div>
                  </div>
                  <div v-if="isSubscription">
                    <v-switch :label="$t('message.end_on_date')" v-model="subscriptionEndDateCheckbox"></v-switch>
                    <div v-if="subscriptionEndDateCheckbox" class="ml-2">
                      <DatePicker :show-time="false" v-model="subscriptionEndDate" :no-boundries="true">
                      </DatePicker>
                    </div>
                  </div>
                  <div v-if="isSubscription">
                    <v-switch :label="$t('message.end_on_checkins')" v-model="subscriptionEndCheckinsCheckbox">
                    </v-switch>
                    <v-text-field outlined dense v-if="subscriptionEndCheckinsCheckbox" v-model="subscriptionCheckins"
                      :label="$t('message.subscription_checkins')" :rules="[validationRules.number]"></v-text-field>
                    <span v-if="subscriptionEndCheckinsCheckbox" class="supporting-text">{{
                      $t('message.subscription_checkins_explained') }}</span>
                  </div>
                </v-card-text>
              </div>
              <div v-if="product && (product.isMembership || product.type == 'service')">
                <v-divider></v-divider>
                <v-card-text v-if="contracts.length > 0" class="pt-0 pb-0">
                  <v-switch v-model="assignContract" :label="$t('message.assign_contract')"></v-switch>
                  <v-autocomplete v-show="assignContract" v-model="selectedContract" :label="$t('message.contract')"
                    :items="contracts" item-text="text" item-value="value" clearable outlined dense>
                  </v-autocomplete>
                  <span v-if="assignContract && assignContract" class="supporting-text">{{
                    $t('message.assign_contract_explained') }}</span>
                </v-card-text>
              </div>
              <div v-if="product">
                <v-divider></v-divider>
                <v-card-text class="pt-0">
                  <v-switch hide-details v-model="applyDiscount" :label="$t('message.apply_discount')"></v-switch>
                  <v-text-field outlined dense
                    v-show="applyDiscount && (!product.isMembership || extraProducts.length == 0)"
                    v-model="discountPercent" :label="$t('message.discount_percent')"
                    :rules="[validationRules.number]"></v-text-field>
                  <v-text-field hide-details :disabled="discountPercent > 0" dense outlined v-show="applyDiscount"
                    v-model="discountAmount" :label="$t('message.discount_amount')"
                    :rules="[validationRules.decimal]"></v-text-field>
                  <div v-if="applyDiscount && product.type == 'service'">
                    <v-switch hide-details v-model="discountExpires" :label="$t('message.expires')"></v-switch>
                    <v-text-field v-if="discountExpires" class="mt-2" hide-details type="number"
                      v-model="discountDurationInMonths" dense outlined
                      :label="$t('message.duration_in_months')"></v-text-field>
                  </div>
                  <v-card-text class="text-center" v-if="applyDiscount && discountedTotal"
                    :style="'color:' + getColor('blue')">{{
                      $t('message.new_total')
                    }}:
                    {{ discountedTotal }}
                    {{ currency }}</v-card-text>
                </v-card-text>
              </div>
              <div
                v-if="product && (product.isMembership || product.type == 'service') && paymentsModule != 'infinicept' && paymentsModule != 'easypay'">
                <v-divider></v-divider>
                <v-card-text v-if="!isCustomAchPaymentOption()" class="pt-0">
                  <v-switch hide-details v-if="!assignToVisitor" v-model="addExtraProductSwitch"
                    :label="$t('message.add_more_products')"></v-switch>
                  <div v-if="addExtraProductSwitch">
                    <v-autocomplete :return-object="true" @change="addExtraProduct()" v-model="selectedExtraProduct"
                      :items="availableProducts" :label="$t('message.product')" item-text="text" clearable outlined
                      dense>
                    </v-autocomplete>
                    <v-list>
                      <v-list-item v-for="(i, index) in extraProducts" :key="'extra_product_item_' + index" link dense>
                        <v-list-item-avatar>
                          <v-avatar size="25" color="grey lighten-4">
                            <ImageDisplay v-if="i.image" :src="i.image" />
                            <img v-else :src="assets('logo-circular-watermarked.png')" />
                          </v-avatar>
                        </v-list-item-avatar>
                        <v-list-item-content>
                          <v-list-item-title class="mb-1 ml-1 mt-1" style="font-size: 14px;">
                            {{ i.name }}
                            <span class="">{{ currencySymbol + i.amountDecimal }}</span>
                          </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action>
                          <v-switch @change="toggleExtraProductFree(i)" :label="$t('message.free')"></v-switch>
                        </v-list-item-action>
                        <v-list-item-action>
                          <v-btn small icon @click="removeExtraProduct(i)">
                            <v-icon>mdi-trash-can-outline</v-icon>
                          </v-btn>
                        </v-list-item-action>
                      </v-list-item>
                    </v-list>
                    <div v-if="extraProducts.length > 0">
                      <span class="text-subtitle-2">{{ $t('message.total_extra') }}:</span> {{ currencySymbol }}{{
                        totalExtra }}
                    </div>
                  </div>
                  <div class="text-center text-h5">
                    <span class="mr-1">TOTAL:</span>
                    <span v-if="applyDiscount && discountedTotal >= 0">{{ currencySymbol }}{{ discountedTotal != null ?
                      discountedTotal : 0 }}</span>
                    <span v-else>
                      {{ currencySymbol }} {{ totalExtra + product.amountDecimal }}
                    </span>
                  </div>
                </v-card-text>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-card class="mt-2" outlined elevation="0" v-show="false"
          v-if="stripeReady && selectedMember && selectedPaymentOptions.indexOf('card') >= 0">
          <v-card-text class="mt-0 pt-0">
            <v-switch class="mb-0 pb-0" v-model="saveCreditCard" :label="$t('message.save_credit_card')"></v-switch>
            <div v-if="product && product.type == 'service' && paymentMethod"
              class="supporting-text mt-0 pt-0 pb-0 mb-0">
              {{ $t('message.replace_default_payment_method') }}</div>
          </v-card-text>
        </v-card>
        <v-card class="mt-2" elevation="0" outlined v-if="!checkoutInitialized">
          <div v-if="paymentTypes.length > 0">
            <v-card-text class="mb-0">
              <v-select hide-details v-model="selectedPaymentOptions" :items="paymentTypes" small-chips dense attach
                chips :label="$t('message.payment_options')" :multiple="!customAch ? true : false" outlined>
              </v-select>
            </v-card-text>
            <div v-if="isCustomAchPaymentOption()">
              <div v-if="selectedPaymentOptions.indexOf('nz_bank_account') >= 0">
                <v-card outlined elevation="0">
                  <v-card-text class="text-center">
                    <div class="text-overline">11-0000-00000000-10</div>
                    <div class="text-caption">Bank Code - Branch Code - Account Number - Suffix</div>
                  </v-card-text>
                  <v-card-text>
                    <v-text-field outlined dense label="Bank Code" v-model="buyer.bank_code">
                    </v-text-field>
                    <v-text-field outlined dense label="Branch Code" v-model="buyer.branch_code">
                    </v-text-field>
                    <v-text-field outlined dense label="Account Number" v-model="buyer.account_number">
                    </v-text-field>
                    <v-text-field hide-details outlined dense label="Suffix" v-model="buyer.suffix">
                    </v-text-field>
                  </v-card-text>
                </v-card>
              </div>
              <v-alert v-else class="ma-2" type="warning">
                Please use Instant Verification only. Micro-deposits are not available at this time. If you have any
                questions, please contact your account manager.
              </v-alert>
            </div>
          </div>
          <div>
            <v-card-actions v-if="!paymentsReady">
              <PaymentTypeSelect v-model="paidWith"></PaymentTypeSelect>
            </v-card-actions>
            <v-card-actions v-if="!isSmallerDisplay() && product" v-allowed="'sell_items'">
              <v-btn outlined small elevation="0"
                :disabled="!stripeReady || handlingCharges || productId == 'manual-membership'" v-if="paymentsReady &&
                  product &&
                  product.amount > 0 &&
                  !subscriptionStartDateCheckbox &&
                  discountedTotal != 0 &&
                  validQuantity &&
                  (email || selectedMember)
                " color="primary" @click="initCheckout()">
                <v-icon>{{ isCustomAchPaymentOption() ? 'mdi-bank-circle-outline' : 'mdi-credit-card-outline'
                  }}</v-icon>
                <span>{{ $t('message.process_here') }}</span>
              </v-btn>
              <v-btn outlined small elevation="0" color="primary" :disbaled="handlingCharges"
                v-if="!automaticTaxes && product &&
                  product.amount > 0 && paymentsReady && paymentMethod && !paymentMethod.expired && product.type != 'service'" @click="savedCardPurchase()">
                <v-icon v-if="isAchPaymentType(paymentMethod.type)">mdi-bank</v-icon>
                <v-icon v-else>mdi-credit-card-check-outline</v-icon>
                <span v-if="isAchPaymentType(paymentMethod.type)">{{ $t('message.pay_saved') }} {{ paymentMethod.type
                  }}</span>
                <span v-else>{{ $t('message.pay_saved_card') }}</span>
              </v-btn>
              <v-btn outlined elevation="0" :disabled="!stripeReady || handlingCharges || addProratedAmount" small
                v-if="(email || selectedMember) && terminalAvailable && canUseTerminal && discountedTotal != 0 && (product.isMembership || product.type == 'service')"
                color="primary" @click="initTerminalCheckout()">
                <v-icon>mdi-calculator</v-icon>
                {{ $t('message.pay_with_terminal') }}
              </v-btn>
              <v-btn outlined small elevation="0" :disabled="!stripeReady || handlingCharges" v-if="paymentsReady &&
                product && product.amount > 0 && validQuantity && selectedMember && !subscriptionStartDateCheckbox &&
                discountedTotal != 0
              " color="primary" @click="initCheckout(true)">
                <v-icon>mdi-cellphone</v-icon>
                <span>{{ $t('message.send_request') }}</span>
              </v-btn>
              <v-btn :disabled="handlingCharges" small elevation="0" v-if="
                validQuantity &&
                (email || selectedMember) &&
                product &&
                product.type == 'good'
              " color="primary" outlined @click="payCash()">
                <PaymentType v-if="!paymentsReady" size="20" :type="paidWith"></PaymentType>
                <v-icon v-else>mdi-cash-100</v-icon>
                <span v-if="!paymentsReady">
                  {{ $t('message.paid_with') }} {{ paidWith }}
                </span>
                <span v-else>
                  {{ $t('message.pay_cash') }}
                </span>
              </v-btn>
              <v-spacer></v-spacer>
              <v-btn small elevation="0" @click="cancel()">
                {{ $t("message.cancel") }}
              </v-btn>
            </v-card-actions>
            <v-card-actions v-else-if="product" v-allowed="'sell_items'">
              <v-menu offset-y top>
                <template v-slot:activator="{ on }">
                  <v-btn outlined
                    :disabled="handlingCharges || (!email && !selectedMember) || productId == 'manual-membership' ? true : false"
                    elevation="0" v-on="on" small slot="activator" color="primary">
                    {{ $t("message.actions") }}
                  </v-btn>
                </template>
                <v-list v-if="product">
                  <v-list-item @click="initCheckout()" v-if="
                    stripeReady && product && !subscriptionStartDateCheckbox &&
                    product.amount > 0 &&
                    validQuantity &&
                    discountedTotal != 0 &&
                    (email || selectedMember)">
                    <v-list-item-title>
                      <v-icon>mdi-credit-card-outline</v-icon>
                      <span>{{ $t('message.process_here') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="!automaticTaxes && product &&
                      product.amount > 0 && paymentsReady && paymentMethod && !paymentMethod.expired && product.type != 'service'"
                    @click="savedCardPurchase()">
                    <v-list-item-title>
                      <v-icon v-if="isAchPaymentType(paymentMethod.type)">mdi-bank</v-icon>
                      <v-icon v-else>mdi-credit-card-check-outline</v-icon>
                      <span v-if="isAchPaymentType(paymentMethod.type)">{{ $t('message.pay_saved') }} {{
                        paymentMethod.type
                        }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="stripeReady &&
                    product && product.amount > 0 && validQuantity && !subscriptionStartDateCheckbox &&
                    discountedTotal != 0 && selectedMember" @click="initCheckout(true)">
                    <v-list-item-title>
                      <v-icon>mdi-cellphone</v-icon>
                      <span>{{ $t('message.send_request') }}</span>
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item v-if="validQuantity &&
                    (email || selectedMember) &&
                    product &&
                    product.type == 'good'" @click="payCash()">
                    <v-list-item-title>
                      <PaymentType v-if="!paymentsReady" size="20" :type="paidWith"></PaymentType>
                      <v-icon v-else>mdi-cash-100</v-icon>
                      <span v-if="!paymentsReady">
                        {{ $t('message.paid_with') }} {{ paidWith }}
                      </span>
                      <span v-else>
                        {{ $t('message.pay_cash') }}
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-spacer>
              </v-spacer>
              <v-btn elevation="0" @click="cancel()">{{ $t("message.cancel") }}</v-btn>
            </v-card-actions>
          </div>
          <v-card-text v-if="handlingCharges" class="text-center">
            <circles-loader></circles-loader>
          </v-card-text>
          <v-card-text v-if="errorMessage" class="text-center text-caption error--text">
            {{ errorMessage }}
          </v-card-text>
        </v-card>
        <v-card v-if="checkoutInitialized" elevation="0" outlined class="text-center mt-2">
          <v-card-text color="success">
            <div class="text-subtitle-2 mb-1">{{ $t("message.charge_notification") }}</div>
            <v-btn v-if="back" elevation="0" small @click="goBack()">{{
              $t("message.back") }}
            </v-btn>
          </v-card-text>
        </v-card>
      </div>
    </div>
    <v-dialog :persistent="true" transition="dialog-bottom-transition" v-model="waitingForReaderPayment"
      max-width="70%">
      <v-card class="pa-0 pt-2" elevation="0" outlined>
        <v-card-text class="pa-0">
          <div class="text-h6 text-center mb-2">{{ $t("message.waiting_terminal_payment") }}</div>
          <div class="text-center">
            <v-progress-linear striped height="50" indeterminate color="success"></v-progress-linear>
          </div>
        </v-card-text>
        <v-card-actions class="mt-2 mb-2">
          <v-spacer></v-spacer>
          <v-btn elevation="0" class="outlined-btn" @click="cancelTerminalTransaction()">{{ $t('message.cancel')
            }}</v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CirclesLoader from "@/components/displays/CirclesLoader.vue";
import PaymentType from "@/components/displays/PaymentTypeDisplay.vue";
import DatePicker from "@/components/inputs/DateTimePicker.vue";
import PaymentTypeSelect from "@/components/inputs/PaymentTypeSelect.vue";
import Api from "@/services/Api";
import Api2 from "@/services/Api2";
import { Environment } from "@/services/Environment";

export default {
  data() {
    return {
      productId: this.$route.params.productId,
      packageId: this.$route.query.packageId,
      visitorEmail: this.$route.query.visitorEmail,
      selectedMember: this.$route.query.userId
        ? this.$route.query.userId
        : null,
      loading: true,
      stripeSessionId: null,
      errorMessage: null,
      addExtraProductSwitch: false,
      isSubscription: false,
      showMOreOptions: false,
      subscriptionEndDate: null,
      subscriptionCheckins: null,
      subscriptionEndDateCheckbox: false,
      subscriptionEndCheckinsCheckbox: false,
      subscriptionStartDate: null,
      subscriptionStartDateCheckbox: false,
      subscriptionStartCheckinsCheckbox: false,
      subscriptionStartDatePickerDialog: null,
      checkoutInitialized: false,
      product: null,
      extraOptionsPanel: null,
      startInFuture: false,
      startInAcademyDate: false,
      datePickerDialog: null,
      paymentTypes: [],
      selectedPaymentOptions: [],
      subscriptionEndDatePickerDialog: false,
      validQuantity: true,
      saleForm: true,
      addProratedAmount: false,
      members: [],
      back: false,
      assignToMember: false,
      assignToVisitor: false,
      quantity: 1,
      academyImage: null,
      paymentForm: false,
      paymentMethod: null,
      currency: null,
      paidWith: null,
      currencySymbol: '$',
      hideQuantity: false,
      trialPeriodDays: 0,
      saveCreditCard: true,
      trialPeriodDate: null,
      loadingPackages: false,
      academyBillDay: null,
      futureBillDateString: null,
      proratedAmount: null,
      paymentsModule: null,
      proratedDays: null,
      proratedAmountPerDay: null,
      contracts: [],
      selectedSubscription: null,
      selectedContract: null,
      email: null,
      subscriptions: [],
      availableProducts: [],
      availablePackages: [],
      selectedPackage: null,
      stripeInstance: null,
      clientSecret: null,
      stripeCustomerId: null,
      paymentIntentId: null,
      customAch: false,
      customAchData: null,
      customAchPostData: null,
      mainStripeAccountId: null,
      stripeReady: false,
      handlingCharges: false,
      terminalStripeAccountId: null,
      paymentsReady: false,
      automaticTaxes: false,
      canUseTerminal: true,
      altStripeAccount: null,
      extraProducts: [],
      selectedExtraProduct: null,
      totalExtra: 0,
      applyDiscount: false,
      assignContract: false,
      discountPercent: null,
      discountAmount: null,
      discountedTotal: null,
      terminal: null,//Stripe terminal with the data
      terminalAvailable: false,
      terminalLocation: null,
      readerInUse: null,
      waitingForReaderPayment: false,
      readerDisconnected: false,
      simulatedTerminal: false,
      discountDurationInMonths: null,
      discountExpires: null
    };
  },
  components: {
    CirclesLoader,
    PaymentTypeSelect,
    PaymentType,
    DatePicker
  },
  created() {
    const now = new Date();
    now.setDate(now.getDate() + 3);
    this.simulatedTerminal = Environment.get("VUE_APP_SIMULATED_TERMINAL") == "true" ? true : false;
    console.log('Started page with simulated terminal mode: ', this.simulatedTerminal)
    Api()
      .get(
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/is_connected/all"
      )
      .then((response) => {
        this.stripeReady = response.data.stripeConnected;
        if (!this.stripeReady && response.data.paymentsModule == 'easypay' && response.data.paymentsReady) {
          this.stripeReady = true;
        }
        if (this.selectedMember) {
          this.getPaymentMethod();
        }
      });
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy)
      .then((response) => {
        this.currency = response.data.currency ? response.data.currency.toUpperCase() : 'USD';
        this.currencySymbol = response.data.currencySymbol ? response.data.currencySymbol : '$';
        this.paymentsModule = response.data.paymentsModule;
        this.academyImage = response.data.image;
        this.mainStripeAccountId = response.data.stripeAccountId;
        this.customAch = response.data.customAch;
        this.academyBillDay = response.data.academyBillDay;
        this.paymentsReady =
          response.data.paymentsReady && response.data.stripeAccountId;
        if (!this.paymentsReady && response.data.paymentsModule == 'easypay')
          this.paymentsReady = true;
        if (!this.paymentsReady && response.data.paymentsModule == 'infinicept')
          this.paymentsReady = true;
        if (!this.paymentsReady)
          this.paidWith = 'cash';
        this.automaticTaxes = response.data.configuration ? response.data.configuration.automaticTaxCalculation : false;
        if (this.productId == "membership" || this.productId == "manual-membership") {
          this.getPackages();
          this.validQuantity = true;
          if (this.productId == "membership")
            this.assignToMember = true;
          else if (this.productId == "manual-membership")
            this.assignToVisitor = true;
          Api()
            .get(
              "admins/academy/" + this.$store.state.managedAcademy + "/products "
            )
            .then((response) => {
              const data = response.data;
              if (data) {
                this.loading = false;
                if (this.productId == "membership") {
                  if (this.paymentsReady && data.subscriptions && data.subscriptions.length > 0) {
                    this.subscriptions.push({ header: 'Automatic' });
                    for (const p of data.subscriptions) {
                      this.subscriptions.push({ value: p._id, text: p.name, frequency: p.interval, automatic: true, amountDecimal: p.amountDecimal, customDaysInterval: p.customDaysInterval });
                    }
                  }
                }
                if (this.visitorEmail)
                  this.email = this.visitorEmail;
                if (data.goods && data.goods.length > 0) {
                  this.subscriptions.push({ header: 'Manual' });
                  for (const p of data.goods) {
                    if (p.isMembership && p.active) {
                      this.subscriptions.push({ value: p._id, text: p.name, frequency: 'manual', automatic: false, amountDecimal: p.amountDecimal });
                    }
                  }
                }
              }
            })
            .catch((e) => {
              if (e)
                this.showAlert("error", this.$t("message.error_generic"));
            });
        } else if (this.productId == "package") {
          this.loadProductsPackage();
        } else {
          this.loadProduct();
        }
      });
    Api()
      .get("admins/academy/" + this.$store.state.selectedAcademy + "/members")
      .then((response) => {
        const data = response.data;
        if (data) {
          for (const m of data.approved) {
            this.members.push({ value: m._id, text: m.fName + " " + m.lName });
          }
        }
      })
      .catch((e) => {
        if (e)
          this.showAlert("error", this.$t("message.error_generic"));
      });
  },
  mounted() {
    let stripeTerminalScript = document.createElement('script')
    stripeTerminalScript.setAttribute('src', 'https://js.stripe.com/terminal/v1/')
    document.head.appendChild(stripeTerminalScript);
  },
  watch: {//getDiscountedPrice
    discountPercent: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.product && this.quantity) {
          let originalAmount = this.product.amountDecimal * this.quantity + this.totalExtra;
          this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue);
        }
      },
    },
    discountAmount: {
      handler: function (newValue) {
        if (!isNaN(newValue) && this.product && this.quantity) {
          let originalAmount = this.product.amountDecimal * this.quantity + this.totalExtra;
          this.discountedTotal = this.getDiscountedPrice(originalAmount, newValue, newValue);
        }
      },
    },
    quantity: {
      handler: function (newValue) {
        this.checkAmountValidity(newValue);
      },
    },
    selectedSubscription: {
      handler: function (newValue) {
        this.errorMessage = null;
        if (newValue) {
          this.productId = newValue;
          this.loadProduct();
        } else {
          this.productId = "subscription";
          this.product = null;
        }
      },
    },
    selectedMember: {
      immediate: true,
      handler: function (newValue) {
        if (newValue) {
          if (this.paymentsReady)
            this.getPaymentMethod();
          this.validateMember()
        }
      },
    },
    startInAcademyDate: {
      handler: function (newValue) {
        if (newValue) {
          this.trialPeriodDate = null;
          this.startInFuture = false;
          this.processProratedAmount();
        }
      },
    },
    trialPeriodDate: {
      handler: function (newValue) {
        if (newValue && this.addProratedAmount) {
          this.processProratedAmount();
        }
      },
    },
    addProratedAmount: {
      handler: function (newValue) {
        if (newValue && this.trialPeriodDate) {
          this.processProratedAmount();
        }
      },
    },
    startInFuture: {
      handler: function (newValue) {
        if (newValue) {
          this.startInAcademyDate = false;
        }
      },
    },
  },
  methods: {
    isAtLeastThreeDays(futureDate) {
      const today = new Date();
      const targetDate = new Date(futureDate);
      today.setHours(0, 0, 0, 0);
      targetDate.setHours(0, 0, 0, 0);
      const diffTime = targetDate.getTime() - today.getTime();
      const ONE_DAY_MS = 86400000;
      const diffDays = Math.floor(diffTime / ONE_DAY_MS);
      return diffDays >= 3;
    },
    selectProductsPackage(e) {
      this.packageId = e;
      this.loadProductsPackage();
    },
    getTransactionTotal() {
      let total = 0;
      if (this.applyDiscount && this.discountedTotal) {
        total = this.discountedTotal;
      } else {
        total = this.totalExtra + this.product.amountDecimal;
      }
      return total;
    },
    removeExtraProduct(i) {
      let index = 0;
      for (const p of this.extraProducts) {
        if (p._id == i._id) {
          this.extraProducts.splice(index, 1);
          break;
        }
        index++;
      }
      this.calculateTotalExtra();
    },
    toggleExtraProductFree(i) {
      if (i.free)
        i.free = false;
      else
        i.free = true;
      this.calculateTotalExtra();
    },
    calculateTotalExtra() {
      let total = 0;
      for (const p of this.extraProducts) {
        if (!p.free) {
          total += p.amountDecimal;
        } else {
          total += 0;
        }
      }

      this.totalExtra = total;
      if (this.discountAmount > 0 || this.discountPercent > 0) {
        this.discountedTotal = this.getDiscountedPrice(this.product.amountDecimal + total, this.discountPercent, this.discountAmount);
      }
    },
    addExtraProduct() {
      const p = this.selectedExtraProduct;
      if (p) {
        this.extraProducts.push(p.product);
        this.calculateTotalExtra();
      }
    },
    getExtraProducts() {
      Api()
        .get(
          "admins/academy/" + this.$store.state.managedAcademy + "/products?category=retail&check_availability=true"
        )
        .then((response) => {
          const data = response.data;
          if (data && this.product) {
            this.availableProducts = [];
            for (const i of data.goods) {
              let taxMatches = true;
              if (this.product.taxBehavior && i.taxBehavior != this.product.taxBehavior)
                taxMatches = false;
              if (taxMatches) {
                this.availableProducts.push({ text: i.name + ' (' + (this.currency + i.amountDecimal) + ')', value: i._id, product: i })
              }
            }
          }
        })
        .catch((e) => {
          console.log('859: ', e)
          this.loading = false;
          if (e)
            this.showAlert(
              "error",
              "Oops!",
              this.$t("message.error_generic")
            );
        });
    },
    cancelTerminalTransaction() {
      if (this.readerInUse) {
        const vm = this;
        try {
          this.terminal.cancelCollectSetupIntentPaymentMethod().then(function (result) {
            if (result.error) {
              vm.waitingForReaderPayment = false;
              vm.readerInUse = null;
              vm.handlingCharges = false;
            } else {
              // Placeholder for processing result.paymentIntent
              vm.waitingForReaderPayment = false;
              vm.readerInUse = null;
              vm.handlingCharges = false;
            }
          });
        } catch (e) {
          console.log('886: ', e);
          vm.waitingForReaderPayment = false;
          vm.readerInUse = null;
          vm.handlingCharges = false;
        }
      }
    },
    getDiscountExpirationDate() {
      let d = null;
      if (this.discountDurationInMonths) {
        const expDate = new Date();
        expDate.setMonth(expDate.getMonth() + new Number(this.discountDurationInMonths).valueOf())
        d = expDate;
      }
      return d;
    },
    initTerminalCheckout() {
      this.waitingForReaderPayment = false;
      if (this.handlingCharges)
        return;
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      this.back = false;
      this.handlingCharges = true;
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }

      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      let prorated = null;
      if (this.product.type == "service" && this.startInAcademyDate && this.proratedAmount > 0) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
        const today = new Date();
        const academyBillDate = new Date();
        academyBillDate.setDate(this.academyBillDay);
        academyBillDate.setHours(1);
        const daysBetween = this.daysBetween(academyBillDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          academyBillDate.setMonth(academyBillDate.getMonth() + 1);
        }
        this.trialPeriodDate = new Date(academyBillDate);
      } else if (this.product.type == "service" && this.startInFuture && this.addProratedAmount) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
      }
      if (this.trialPeriodDate) {
        this.trialPeriodDate.setHours(4);
        if (!this.isAtLeastThreeDays(this.trialPeriodDate)) {
          this.showAlert("error", this.$t("message.trial_end_too_close"));
          this.handlingCharges = false;
          return;
        }
      }
      const discountExpirationDate = this.getDiscountExpirationDate();
      let postData = {
        product: this.productId,
        processedBy: "terminal",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        discountedTotal: this.discountedTotal,
        discountDuration: this.discountDurationInMonths ? new Number(this.discountDurationInMonths).valueOf() : null,
        discountExpirationDate: discountExpirationDate,
        quantity: 1,
        prorated: prorated,
        buyer: this.selectedMember,
        email: this.email,
        documentTemplate: this.selectedContract,
        manualRecurrence: this.product.manualRecurrence,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        trialPeriodEndDate: this.startInFuture || this.startInAcademyDate ? this.trialPeriodDate : null,
      };
      let postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/terminals/init/payment";
      if (this.paymentsModule == 'infinicept') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.getTransactionTotal();
        this.navigateTo({
          name: "inifnicept-checkout",
          params: { transactionData: postData }
        });
        return;
      } else if (this.paymentsModule == 'easypay') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.discountedTotal ? this.discountedTotal : this.transactionTotal;
        postData.paymentsModule = 'easypay';
        postData.routeFrom = 'regularsell';
        postData.productData = this.product;
        this.navigateTo({
          name: "easypay-checkout",
          params: { transactionData: postData }
        });
        return;
      }
      Api()
        .post(postUrl, postData)
        .then((response) => {
          let setupMode = true;
          const clientSecret = response.data.clientSecret;
          postData.customer = response.data.customer;
          if (response.data.setupIntent) {
            postData.setupIntent = response.data.setupIntent;
            setupMode = true;
          } else if (response.data.paymentIntent) {
            postData.paymentIntent = response.data.paymentIntent;
            setupMode = false;
          }

          this.waitingForReaderPayment = true;
          //Now we call the terminal and ask for a setupIntent
          if (setupMode)
            return this.terminalPaymentSetupMode(clientSecret, postData);
          else
            return this.terminalPaymentRegularMode(clientSecret, postData);
        })
        .catch((e) => {
          this.waitingForReaderPayment = false;
          this.handlingCharges = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    terminalPaymentSetupMode(clientSecret, postData) {
      const vm = this;
      this.terminal.collectSetupIntentPaymentMethod(clientSecret, true).then(function (collectResult) {
        if (collectResult.error) {
          if (collectResult.error.code != 'canceled') {
            vm.showAlert("error", vm.$t("message.error_generic"));
            vm.waitingForReaderPayment = false;
            vm.handlingCharges = false;
          }
        } else {
          // Placeholder for confirming result.setupIntent
          vm.terminal.confirmSetupIntent(collectResult.setupIntent).then(function (processSetupResult) {
            if (processSetupResult.error) {
              vm.showAlert("error", vm.$t("message.error_generic"));
              vm.waitingForReaderPayment = false;
              vm.handlingCharges = false;
            } else if (processSetupResult.setupIntent) {
              vm.completeTerminalPurchase(postData);
            }
          });
        }
      });
    },
    terminalPaymentRegularMode(clientSecret, postData) {
      const vm = this;
      this.terminal.collectPaymentMethod(clientSecret).then(function (collectResult) {
        if (collectResult.error) {
          vm.handlingCharges = false;
          vm.waitingForReaderPayment = false;
          if (collectResult.error.code != 'canceled')
            vm.showAlert("error", vm.$t("message.error_generic"));
        } else {
          // Placeholder for processing result.paymentIntent
          vm.terminal.processPayment(collectResult.paymentIntent).then(function (processPaymentResult) {
            if (processPaymentResult.error) {
              vm.showAlert("error", vm.$t("message.error_generic"));
            } else if (processPaymentResult.paymentIntent) {
              vm.completeTerminalPurchase(postData);
              vm.waitingForReaderPayment = false;
              vm.clearCart();
              vm.handlingCharges = false;
            }
          });
        }
      });
    },
    completeTerminalPurchase(postData) {
      //Send it to the back end to record our transaction
      Api()
        .post(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/terminals/complete/subscription", postData
        )
        .then((response) => {
          if (response.data) {
            this.waitingForReaderPayment = false;
            this.handlingCharges = false;
            this.showAlert(
              "success",
              this.$t("message.payment_completed")
            );
            this.navigateTo({ name: 'viewtransaction', params: { sessionId: response.data.transaction._id } });
          }
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.waitingForReaderPayment = false;
          this.handlingCharges = false;
        });
    },
    getTerminalAvailability() {
      Api()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/terminals/"
        )
        .then((response) => {
          if (response.data && response.data.locations && response.data.locations.length > 0) {
            //If we have a connected reader, connect, if not, move on
            this.terminalLocation = response.data.locations[0].id;
            this.terminalStripeAccountId = response.data.locations[0].stripeAccount;
            if (this.terminalStripeAccountId != this.mainStripeAccountId) {
              this.canUseTerminal = false;
            }
            if (this.product) {
              if (this.product.altStripeAccount && this.product.altStripeAccount == this.terminalStripeAccountId)
                this.connectToReader();
              else if (!this.product.altStripeAccount)
                this.connectToReader();
            }
          } else if (this.simulatedTerminal) {
            console.log('No terminals available but we are on simulated terminal mode: ', this.simulatedTerminal)
            this.connectToReader();
          }
        });
    },
    refreshReaderConnection() {
      if (this.terminalLocation || this.simulatedTerminal) {
        this.connectToReader();
      }
    },
    connectToReader() {
      this.readerDisconnected = false;
      this.readerInUse = null;
      const vm = this;
      const loc = this.terminalLocation;
      this.connectingReader = true;
      let config = null;
      if (this.simulatedTerminal) {
        console.log('Skipping terminal connection , simulated reader terminal');
        /*eslint-disable no-undef*/
        this.terminal = StripeTerminal.create({
          onFetchConnectionToken: function () {
            return Api()
              .post("payments/academy/" + vm.$store.state.managedAcademy + "/terminals/connection_token", { simulated: true })
              .then((response) => {
                return response.data.secret;
              })
          },
          onUnexpectedReaderDisconnect: function () {
            vm.readerDisconnected = true;
            vm.showAlert("error", this.$t("message.reader_disconnected"));
          },
        });
        config = { simulated: true };
      } else {
        /*eslint-disable no-undef*/
        this.terminal = StripeTerminal.create({
          onFetchConnectionToken: function () {
            return Api()
              .post("payments/academy/" + vm.$store.state.managedAcademy + "/terminals/connection_token", { location: loc, stripeAccountId: this.terminalStripeAccountId })
              .then((response) => {
                return response.data.secret;
              })
          },
          onUnexpectedReaderDisconnect: function () {
            vm.readerDisconnected = true;
            vm.showAlert("error", this.$t("message.reader_disconnected"));
          },
        });
        config = { simulated: false, location: loc };
      }

      this.terminal.discoverReaders(config).then(function (discoverResult) {
        if (discoverResult.error) {
          vm.showAlert("error", this.$t("message.error_generic"));
          vm.connectingReader = false;
        } else if (discoverResult.discoveredReaders.length === 0) {
          vm.showAlert("error", this.$t("message.no_readers_available"));
          vm.connectingReader = false;
        } else {
          //We are only grabbing 1 reader, the id matches the one we should have gotten from the beginning
          const discoveredReader = discoverResult.discoveredReaders[0];
          //We got a reader, lets connect
          vm.terminal.connectReader(discoveredReader).then(function (connectResult) {
            if (connectResult.error) {
              //vm.showAlert("error", vm.$t("message.connection_error"));
              vm.connectingReader = false;
              vm.terminalAvailable = false;
            } else {
              vm.terminalAvailable = true;
              vm.connectingReader = false;
              vm.canUseTerminal = true;
              vm.readerInUse = discoveredReader.id;
            }
          });
        }
      });
    },
    getPackages() {
      this.loadingPackages = true;
      Api()
        .get("admins/managedacademy/" + this.$store.state.managedAcademy + "/products/packages")
        .then((response) => {
          this.availablePackages = response.data.data;
          this.loadingPackages = false;
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    getPaymentTypes() {
      Api()
        .get(
          "payments/academy/" +
          this.$store.state.managedAcademy +
          "/paymenttypes"
        )
        .then((response) => {
          if (response.data && response.data.paymentMethods && response.data.paymentMethods.length == 2) {
            this.paymentTypes.push({ text: this.$t("message.credit_card"), value: 'card' });
            this.paymentTypes.push({ text: this.$t("message.ach"), value: response.data.paymentMethods[1] });
            this.selectedPaymentOptions.push('card');
            if (this.customAch)
              this.selectedPaymentOptions = 'card';
          }
        });
    },
    validateMember() {
      Api()
        .patch("admins/academy/" + this.$store.state.managedAcademy + "/member/" + this.selectedMember + "/validate");
    },
    processProratedAmount() {
      if (this.product && this.startInAcademyDate && this.academyBillDay && this.academyBillDay > 0) {
        const today = new Date();
        const academyBillDate = new Date();
        academyBillDate.setDate(this.academyBillDay);
        academyBillDate.setHours(1);
        const daysBetween = this.daysBetween(academyBillDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          academyBillDate.setMonth(academyBillDate.getMonth() + 1);
        }
        this.proratedDays = this.daysBetween(academyBillDate, today);
        this.futureBillDateString = this.$d(academyBillDate, 'month_day_year', this.$i18n.locale);

        const monthlyPrice = this.product.amountDecimal;
        const daysLeft = this.proratedDays;
        let totalDays = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
        let pricePerDay = Math.round((monthlyPrice / totalDays) * 100) / 100;
        this.proratedAmount = Math.round(daysLeft * pricePerDay * 100) / 100;
        this.proratedAmountPerDay = pricePerDay;
      } else if (this.product && this.trialPeriodDate) {
        const today = new Date();
        today.setHours(0);
        const futureDate = new Date(this.trialPeriodDate);
        const daysBetween = this.daysBetween(futureDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          futureDate.setMonth(futureDate.getMonth() + 1);
        }
        this.proratedDays = this.daysBetween(futureDate, today);
        this.futureBillDateString = this.$d(futureDate, 'month_day_year', this.$i18n.locale);

        const monthlyPrice = this.product.amountDecimal;
        const daysLeft = this.proratedDays;
        let totalDays = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
        let pricePerDay = Math.round((monthlyPrice / totalDays) * 100) / 100;
        this.proratedAmount = Math.round(daysLeft * pricePerDay * 100) / 100;
        this.proratedAmountPerDay = pricePerDay;
      }
    },
    isMonthlyMembership() {
      if (this.product && this.product.interval == 'month')
        return true;
      else
        return false;
    },
    savedCardPurchase() {
      this.waitingForPayment = true;
      this.handlingCharges = true;


      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }

      this.processTrialPeriod();
      this.back = false;
      this.handlingCharges = true;

      const items = this.setupItems();
      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      if (this.trialPeriodDate)
        this.trialPeriodDate.setHours(4);
      const postData = {
        items: items,
        saveCreditCard: null,
        processedBy: "desk",
        buyer: this.selectedMember,
        productId: this.productId,
        quantity: this.quantity,
        email: this.email,
        transactionTime: new Date(),
        sendNotification: false,
        discountPercent: discountPercent,
        manualRecurrence: this.product.manualRecurrence,
        discountAmount: discountAmount,
        discountedTotal: this.discountedTotal,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        trialPeriodEndDate: this.trialPeriodDate,
        trialPeriodDays:
          this.trialPeriodDate && this.trialPeriodDays > 0
            ? this.trialPeriodDays
            : null,
      };
      let postUrl =
        "payments/academy/" +
        this.$store.state.managedAcademy +
        "/onetime/savedcard";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.navigateTo({
              name: "transactions",
              query: { status: "success" },
            });
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          this.waitingForPayment = false;
          if (e.response.status == 500) {
            const errData = e.response.data;
            this.showAlert(
              "error",
              this.$t("message.error_code") + ": " + errData.type,
              errData.message,
              15000
            );
          } else {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    async getPaymentMethod() {
      try {
        const response = await Api()
          .get(
            "payments/academy/" +
            this.$store.state.managedAcademy +
            "/customer/" +
            this.selectedMember
          );
        if (response) {
          if (response.data.invalid) {
            if (this.paymentsReady) {
              console.log('User does not have a valid stripe customer. Will get createed at moment of sale')
            }
          } else {
            this.buyer = { email: response.data.email, name: response.data.name };
            if (response.data.paymentMethod) {
              this.paymentMethod = response.data.paymentMethod;
              /*
              if (this.product && this.product.type == "service")
                this.saveCreditCard = true;
              */
              if (this.paymentMethod.expYear && this.paymentMethod.expMonth) {
                let expDate = new Date();
                expDate.setFullYear(this.paymentMethod.expYear);
                expDate.setMonth(this.paymentMethod.expMonth - 1);
                this.paymentMethod.expDate = expDate;
                const today = new Date();
                if (expDate > today) this.paymentMethod.expired = false;
                else this.paymentMethod.expired = true;
              }
            } else {
              this.paymentMethod = null;
            }
          }
        }
      } catch (err) {
        this.showAlert("error", this.$t("message.error_generic111"));
      }
    },
    checkAmountValidity(q) {
      if (!isNaN(q) && q != "") {
        const quantityNumber = new Number(q).valueOf();
        if (
          !this.product.unlimited &&
          this.product.availableQuantity != null &&
          this.product.availableQuantity >= 0
        ) {
          //console.log('Is ['+this.product.availableQuantity+'] bigger than ['+quantityNumber+']? ', this.product.availableQuantity >= quantityNumber)
          if (this.product.availableQuantity >= quantityNumber)
            this.validQuantity = true;
          else this.validQuantity = false;
        } else {
          this.validQuantity = true;
        }
      } else {
        this.validQuantity = false;
      }
      if (this.validQuantity && this.quantity <= 0) this.validQuantity = false;
    },
    payCash() {
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      if (!this.product) {
        this.showAlert("error", this.$t("message.fill_all_params"));
        return;
      }
      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionStartDate = this.subscriptionStartDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      this.back = false;
      this.handlingCharges = true;
      const items = this.setupItems();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      }
      const postData = {
        items: items,
        processedBy: "desk",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        buyer: this.selectedMember,
        productId: this.productId,
        quantity: this.quantity,
        email: this.email,
        manualRecurrence: this.product.manualRecurrence,
        documentTemplate: this.selectedContract,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        transactionDate: subscriptionStartDate ? subscriptionStartDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        paidWith: this.paidWith
      };
      const postUrl =
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/payments/onetime/cash";
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            this.navigateTo({ name: 'viewtransaction', params: { sessionId: data.transaction._id } })
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    loadProductsPackage() {
      Api()
        .get("admins/managedacademy/" + this.$store.state.managedAcademy + "/products/packages/" + this.packageId)
        .then((response) => {
          const productsPackage = response.data.data;
          this.product = productsPackage.mainProduct;
          this.productId = productsPackage.mainProduct._id;
          this.loading = false;
          this.loadProductExtras();
          //Handle extra products
          //addExtraProductSwitch
          if (productsPackage.products) {
            this.addExtraProductSwitch = true;
            this.extraOptionsPanel = 0;
            this.extraProducts = [];
            for (const p of productsPackage.products) {
              this.extraProducts.push(p);
            }
            this.calculateTotalExtra();
          }
        })
        .catch((e) => {
          if (e) {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    loadProduct() {
      Api()
        .get(
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/products/" +
          this.productId
        )
        .then((response) => {
          this.product = response.data;
          this.loading = false;
          this.loadProductExtras();
        })
        .catch((e) => {
          this.loading = false;
          console.log('1529: ', e)
          if (e)
            this.showAlert("error", this.$t("message.error_generic"));
        });
    },
    loadProductExtras() {
      if (!this.isMonthlyMembership())
        this.startInAcademyDate = false;
      if (this.product && this.product.type != "good" && this.paymentTypes.length == 0) {
        this.getPaymentTypes();
        this.validQuantity = true;
      }
      if (this.product && this.product.isMembership) {
        this.quantity = 1;
        this.hideQuantity = true;
        if (this.stripeReady && this.paymentsModule != 'infinicept' && !this.isSmallerDisplay()) {
          this.getTerminalAvailability();
        }
        this.getExtraProducts();
      }
      if (this.product && this.product.type == "good" && this.product.altStripeAccount)
        this.altStripeAccount = this.product.altStripeAccount;
      if (this.product.isMembership) {
        Api()
          .get("documents/academy/" + this.$store.state.managedAcademy + "/contracts")
          .then((response) => {
            const data = response.data;
            if (data) {
              for (const c of data.contracts)
                this.contracts.push({ value: c._id, text: c.name });
              if (this.product.documentTemplate) {
                this.assignContract = true;
                this.selectedContract = this.product.documentTemplate;
              }
            }
          });
        this.isSubscription = true;
      }
      if (this.product && this.product.type == "good" &&
        (this.product.membershipDays > 0
          || this.product.membershipMonths > 0)) {
        //yyyy-mm-dd
        const date = new Date();
        date.setHours(23);
        date.setMinutes(59);
        if (this.product.membershipDays > 0) {
          date.setDate(date.getDate() + this.product.membershipDays);
        } else if (this.product.membershipMonths > 0) {
          date.setMonth(date.getMonth() + this.product.membershipMonths);
        }
        this.subscriptionEndDate = date;
        this.subscriptionEndDateCheckbox = true;
      } else if (this.product && this.product.type == "good" &&
        this.product.membershipCheckins > 0) {
        this.subscriptionCheckins = this.product.membershipCheckins;
        this.subscriptionEndCheckinsCheckbox = true;
      }
    },
    processTrialPeriod() {
      if (this.trialPeriodDate) {
        const now = new Date();
        const future = this.trialPeriodDate;
        this.trialPeriodDays = Math.abs(this.daysBetween(now, future));
      }
    },
    stringToDate(s) {
      if (s) {
        const future = new Date();
        var split = s.split("-");
        future.setFullYear(split[0]);
        future.setMonth(parseInt(split[1]) - 1);
        future.setDate(split[2]);
        return future;
      }
    },
    async processCustomAchRecurrentPayment(postData, sendNotification) {
      try {
        this.handlingCharges = true;
        /*eslint-disable no-undef*/
        this.stripeInstance = Stripe(Environment.get("VUE_APP_STRIPE_PUBLISHABLE_KEY"), {
          stripeAccount: this.mainStripeAccountId
        });
        if (this.selectedPaymentOptions.indexOf('acss_debit') >= 0) {
          const res = await Api2()
            .post("payments/managedacademy/" +
              this.$store.state.managedAcademy +
              "/ach/init/subscription", postData);
          const resData = res.data;
          this.clientSecret = resData.clientSecret;
          this.stripeCustomerId = resData.customer;
          this.paymentIntentId = resData.paymentIntentId;
          this.customAchPostData.transactionId = resData.transactionId;

          if (sendNotification) {
            if (resData.transactionId)
              this.navigateTo({ name: 'viewtransaction', params: { sessionId: resData.transactionId } });
            else
              console.error('Error processing acss_debit: System culd not start transaction. Contact Support');
            return;
          }

          const { paymentIntent, error } = await this.stripeInstance.confirmAcssDebitPayment(
            this.clientSecret,
            {
              payment_method: {
                billing_details: {
                  name: this.buyer.name,
                  email: this.buyer.email,
                },
              },
            }
          );
          if (error) {
            console.error('Error processing acss_debit: ', error.message);
            this.handlingCharges = false;
            this.showAlert("error", error.message, null, 7000);
          } else if (paymentIntent && paymentIntent.status != 'canceled') {
            this.handlingCharges = true;
            // Send payment_method to backend to attach to subscription
            this.customAchPostData.paymentIntentId = paymentIntent.id;
            this.customAchPostData.status = paymentIntent.status;
            this.customAchPostData.stripeCustomerId = this.stripeCustomerId;
            this.customAchPostData.paymentMethodId = paymentIntent.payment_method;
            this.customAchPostData.skipPaymentConfirmation = true;
            this.completeCustomAchPurchase();
          }
        } else if (this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0) {
          //test: 11-0000-00000000-10
          /*
          this.buyer.account_number = '0000000';
          this.buyer.bank_code = '11';
          this.buyer.branch_code = '0000';
          this.buyer.suffix = '10';
          */
          if (sendNotification) {
            postData.sendNotification = true;
            const res = await Api2()
              .post("payments/managedacademy/" +
                this.$store.state.managedAcademy +
                "/ach/init/subscription", postData);
            const resData = res.data;
            if (resData.transactionId) {
              this.navigateTo({ name: 'viewtransaction', params: { sessionId: resData.transactionId } });
            } else {
              console.error('Error processing acss_debit: System culd not start transaction. Contact Support');
              this.showAlert("error", 'Error processing acss_debit: System culd not start transaction. Contact Support', null, 7000);
            }
            return;
          }
          if (this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0) {
            if (!this.buyer.account_number ||
              !this.buyer.bank_code ||
              !this.buyer.branch_code ||
              !this.buyer.suffix) {
              this.showAlert("error", "Please complete all bank information");
              this.handlingCharges = false;
              return;
            }
          }

          const { paymentMethod, error } = await this.stripeInstance.createPaymentMethod({
            type: 'nz_bank_account',
            nz_bank_account: {
              account_number: this.buyer.account_number, // 7-digit account base
              bank_code: this.buyer.bank_code, // 2 digits
              branch_code: this.buyer.branch_code,     // 4-digit branch code
              suffix: this.buyer.suffix
            },
            billing_details: {
              name: this.buyer.name,
              email: this.buyer.email,
            },
          });
          if (error) {
            console.error('Error processing acss_debit: ', error.message);
            this.handlingCharges = false;
            this.showAlert("error", error.message, null, 7000);
          } else if (paymentMethod) {
            this.handlingCharges = true;
            postData.paymentMethodId = paymentMethod.id;
            const res = await Api2()
              .post("payments/managedacademy/" +
                this.$store.state.managedAcademy +
                "/ach/init/subscription", postData);
            const resData = res.data;
            this.clientSecret = resData.clientSecret;
            this.stripeCustomerId = resData.customer;
            this.paymentIntentId = resData.paymentIntentId;
            this.customAchPostData.paymentIntentId = this.paymentIntentId;
            this.customAchPostData.stripeCustomerId = this.stripeCustomerId;
            if (!this.paymentIntentId) {
              this.showAlert("error", "No payment intent, please contact support.");
              this.handlingCharges = false;
              return;
            }
            this.customAchPostData.transactionId = resData.transactionId;
            if (postData.trialPeriodEndDate)
              this.customAchPostData.paymentMethodConfirmed = true;
            this.completeCustomAchPurchase();
          }
        } else {
          this.showAlert("error", "Invalid ACH payment type: ", this.selectedPaymentOptions);
          this.handlingCharges = false
          return;
        }
      } catch (err) {
        console.log('1735: ', err)
        this.showAlert("error", this.$t("message.error_generic"));
        this.handlingCharges = false
      }
    },
    completeCustomAchPurchase() {
      //Send it to the back end to record our transaction
      Api()
        .post(
          "payments/managedacademy/" +
          this.$store.state.managedAcademy +
          "/ach/complete/subscription", this.customAchPostData
        )
        .then((response) => {
          if (response.data) {
            this.handlingCharges = false;
            this.showAlert(
              "success",
              this.$t("message.payment_completed")
            );
            this.navigateTo({ name: 'viewtransaction', params: { sessionId: response.data.transaction._id } });
          }
        }).catch(() => {
          this.showAlert("error", this.$t("message.error_generic"));
          this.handlingCharges = false;
        });
    },
    initCheckout(sendNotification) {
      this.errorMessage = null;
      if (!this.product) {
        this.showAlert("error", this.$t("message.fill_all_params"));
        return;
      }
      if (this.applyDiscount && this.discountedTotal < 0) {
        this.showAlert("error", this.$t("message.incorrect_discount_number"));
        return;
      }
      //this.paymentForm = true;
      this.handlingCharges = true;
      this.processTrialPeriod();
      this.back = false;
      const items = this.setupItems();
      let subscriptionEndDate = this.subscriptionEndDate;
      let subscriptionCheckins = new Number(this.subscriptionCheckins).valueOf();
      let discountPercent = null;
      let discountAmount = null;
      if (this.applyDiscount && this.discountPercent) {
        try {
          discountPercent = new Number(this.discountPercent).valueOf();
        } catch (err) {
          this.handlingCharges = false;
          this.showAlert("error", this.$t("message.incorrect_discount_number"));
          return;
        }
      } else if (this.applyDiscount && this.discountAmount) {
        try {
          discountAmount = new Number(this.discountAmount).valueOf();
        } catch (err) {
          this.showAlert("error", this.$t("message.trial_end_too_close"));
          this.handlingCharges = false;
          return;
        }
      }
      let prorated = null;
      if (this.product.type == "service" && this.startInAcademyDate && this.proratedAmount > 0) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
        const today = new Date();
        const academyBillDate = new Date();
        academyBillDate.setDate(this.academyBillDay);
        academyBillDate.setHours(1);
        const daysBetween = this.daysBetween(academyBillDate, today);
        if (daysBetween != 0 && daysBetween < 3) {
          //Next month or past
          academyBillDate.setMonth(academyBillDate.getMonth() + 1);
        }
        this.trialPeriodDate = academyBillDate;
      } else if (this.product.type == "service" && this.startInFuture && this.addProratedAmount) {
        prorated = {
          amount: this.proratedAmount,
          days: this.proratedDays,
          pricePerDay: this.proratedAmountPerDay
        };
      }
      //Add one for NZ
      if (this.trialPeriodDate) {
        this.trialPeriodDate.setHours(4);//Set the start to the morning
        if (!this.isAtLeastThreeDays(this.trialPeriodDate)) {
          this.showAlert("error", this.$t("message.trial_end_too_close"));
          this.handlingCharges = false;
          return;
        }
      }
      const discountExpirationDate = this.getDiscountExpirationDate();
      let postData = {
        items: items,
        saveCreditCard: this.saveCreditCard ? this.saveCreditCard : null,
        processedBy: sendNotification ? "user" : "desk",
        discountPercent: discountPercent,
        discountAmount: discountAmount,
        discountDuration: this.discountDurationInMonths ? new Number(this.discountDurationInMonths).valueOf() : null,
        discountExpirationDate: discountExpirationDate,
        buyer: this.selectedMember,
        prorated: prorated,
        productId: this.productId,
        quantity: this.quantity,
        email: this.email,
        transactionTime: new Date(),
        paymentTypes: this.customAch ? [this.selectedPaymentOptions] : this.selectedPaymentOptions,
        manualRecurrence: this.product.manualRecurrence,
        sendNotification: sendNotification,
        documentTemplate: this.selectedContract,
        subscriptionEndDate: subscriptionEndDate ? subscriptionEndDate : null,
        subscriptionCheckins: subscriptionCheckins ? subscriptionCheckins : null,
        trialPeriodEndDate: this.startInFuture || this.startInAcademyDate ? this.trialPeriodDate : null,
        /*
        trialPeriodDays:
          this.trialPeriodDate && this.trialPeriodDays > 0
            ? this.trialPeriodDays
            : null,
        */
      };
      if (this.customAch && this.isCustomAchPaymentOption()) {
        this.customAchPostData = postData;
        this.customAchPostData.paymentType = this.selectedPaymentOptions;//This is not an array
        return this.processCustomAchRecurrentPayment(postData, sendNotification);
      }
      if (this.paymentsModule == 'infinicept') {
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.getTransactionTotal();
        postData.paymentsModule = 'infinicept';
        this.navigateTo({
          name: "inifnicept-checkout",
          params: { transactionData: postData }
        });
        return;
      } else if (this.paymentsModule == 'easypay') {
        postData.interval = this.product && this.product.interval ? this.product.interval : null;
        postData.currencySymbol = this.currencySymbol;
        postData.total = this.getTransactionTotal();
        postData.paymentsModule = 'easypay';
        postData.routeFrom = 'regularsell';
        postData.productData = this.product;
        postData.sendNotification = sendNotification;
        return this.navigateTo({
          name: "easypay-checkout",
          params: { transactionData: postData }
        });
      }
      let postUrl =
        "admins/academy/" +
        this.$store.state.managedAcademy +
        "/payments/onetime/checkout";
      if (this.product.type == "service") {
        postUrl =
          "admins/academy/" +
          this.$store.state.managedAcademy +
          "/payments/recurrent/checkout";
      }
      Api()
        .post(postUrl, postData)
        .then((response) => {
          const data = response.data;
          if (data) {
            if (!sendNotification) {
              if (this.altStripeAccount)
                this.navigateTo({
                  name: "striperedirect_session",
                  params: { sessionId: data.id },
                  query: { altStripeAccount: this.altStripeAccount }
                });
              else
                this.navigateTo({
                  name: "striperedirect_session",
                  params: { sessionId: data.id },
                });
            } else {
              this.handlingCharges = false;
              this.checkoutInitialized = true;
              this.back = true;
              /*
              this.showAlert(
                "success",
                this.$t("message.success"),
                this.$t("message.charge_notification"),
                8000
              );
              */
              //If send notification, go to transaction!
              if (data.transactionId)
                this.navigateTo({ name: 'viewtransaction', params: { sessionId: data.transactionId } });
              else
                this.goBack();
            }
          }
        })
        .catch((e) => {
          this.handlingCharges = false;
          if (e.response.data.localizedMessage) {
            this.errorMessage = this.$t("message." + e.response.data.localizedMessage);
            this.showAlert("error", this.errorMessage);
          } else if (e.response.data.message) {
            this.showAlert("error", e.response.data.message);
          } else {
            this.showAlert("error", this.$t("message.error_generic"));
          }
        });
    },
    isCustomAchPaymentOption() {
      const isArray = Array.isArray(this.selectedPaymentOptions);
      if (!isArray && (this.selectedPaymentOptions.indexOf('acss_debit') >= 0 ||
        this.selectedPaymentOptions.indexOf('nz_bank_account') >= 0 ||
        this.selectedPaymentOptions.indexOf('ach') >= 0)) {
        return true;
      }
      return false;
    },
    setupItems() {
      try {
        let items = [];
        for (const i of this.extraProducts) {
          let image = this.userImg(this.academyImage);
          if (i.image) image = this.userImg(i.image);
          items.push({
            name: i.name,
            description:
              !i.description || i.description == "" ? " " : i.description,
            images: [image],
            amount: i.amount,
            amountDecimal: i.free ? 0 : i.amountDecimal,
            currency: this.currency,
            quantity: 1,
            product: i._id,
          });
        }
        return items;
      } catch (err) {
        console.log('1953: ', err);
      }
      return [];
    },
    cancel() {
      if (this.stripeSessionId) {
        this.handlingCharges = true;
        this.navigateTo({ name: "products" });
      } else {
        this.navigateTo({ name: "products" });
      }
    },
  },
};
</script>

<style scoped></style>
